import { create } from 'zustand';
import { teamService } from '@/services/team.service';
import type { TeamMember, TeamInvitation, TeamRole, OrgMembership } from '@/types';

interface TeamState {
  // Members
  members: TeamMember[];
  membersLoading: boolean;
  fetchMembers: () => Promise<void>;
  updateMemberRole: (userId: string, role: string) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;

  // Invitations
  invitations: TeamInvitation[];
  invitationsLoading: boolean;
  fetchInvitations: () => Promise<void>;
  inviteMember: (email: string, role: string) => Promise<TeamInvitation>;
  revokeInvitation: (id: string) => Promise<void>;

  // My Invitations
  myInvitations: TeamInvitation[];
  fetchMyInvitations: () => Promise<void>;
  acceptInvitation: (token: string) => Promise<void>;
  declineInvitation: (token: string) => Promise<void>;

  // Roles
  roles: TeamRole[];
  rolesLoading: boolean;
  fetchRoles: () => Promise<void>;
  createRole: (data: { name: string; description?: string; permissions: Record<string, boolean> }) => Promise<TeamRole>;
  updateRole: (id: string, data: { name?: string; description?: string; permissions?: Record<string, boolean> }) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;

  // Organizations
  organizations: OrgMembership[];
  fetchOrganizations: () => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
}

export const useTeamStore = create<TeamState>((set) => ({
  // Members
  members: [],
  membersLoading: false,

  fetchMembers: async () => {
    set({ membersLoading: true });
    try {
      const members = await teamService.getMembers();
      set({ members, membersLoading: false });
    } catch {
      set({ membersLoading: false });
      throw new Error('Failed to fetch members');
    }
  },

  updateMemberRole: async (userId, role) => {
    await teamService.updateMemberRole(userId, role);
    set((state) => ({
      members: state.members.map((m) => (m.user_id === userId ? { ...m, role } : m)),
    }));
  },

  removeMember: async (userId) => {
    await teamService.removeMember(userId);
    set((state) => ({
      members: state.members.filter((m) => m.user_id !== userId),
    }));
  },

  // Invitations
  invitations: [],
  invitationsLoading: false,

  fetchInvitations: async () => {
    set({ invitationsLoading: true });
    try {
      const invitations = await teamService.getInvitations();
      set({ invitations, invitationsLoading: false });
    } catch {
      set({ invitationsLoading: false });
      throw new Error('Failed to fetch invitations');
    }
  },

  inviteMember: async (email, role) => {
    const invitation = await teamService.inviteMember(email, role);
    set((state) => ({ invitations: [invitation, ...state.invitations] }));
    return invitation;
  },

  revokeInvitation: async (id) => {
    await teamService.revokeInvitation(id);
    set((state) => ({
      invitations: state.invitations.filter((i) => i.id !== id),
    }));
  },

  // My Invitations
  myInvitations: [],

  fetchMyInvitations: async () => {
    const myInvitations = await teamService.getMyInvitations();
    set({ myInvitations });
  },

  acceptInvitation: async (token) => {
    await teamService.acceptInvitation(token);
    set((state) => ({
      myInvitations: state.myInvitations.filter((i) => i.token !== token),
    }));
    const organizations = await teamService.getOrganizations();
    set({ organizations });
  },

  declineInvitation: async (token) => {
    await teamService.declineInvitation(token);
    set((state) => ({
      myInvitations: state.myInvitations.filter((i) => i.token !== token),
    }));
  },

  // Roles
  roles: [],
  rolesLoading: false,

  fetchRoles: async () => {
    set({ rolesLoading: true });
    try {
      const roles = await teamService.getRoles();
      set({ roles, rolesLoading: false });
    } catch {
      set({ rolesLoading: false });
      throw new Error('Failed to fetch roles');
    }
  },

  createRole: async (data) => {
    const role = await teamService.createRole(data);
    set((state) => ({ roles: [role, ...state.roles] }));
    return role;
  },

  updateRole: async (id, data) => {
    const updated = await teamService.updateRole(id, data);
    set((state) => ({
      roles: state.roles.map((r) => (r.id === id ? updated : r)),
    }));
  },

  deleteRole: async (id) => {
    await teamService.deleteRole(id);
    set((state) => ({
      roles: state.roles.filter((r) => r.id !== id),
    }));
  },

  // Organizations
  organizations: [],

  fetchOrganizations: async () => {
    const organizations = await teamService.getOrganizations();
    set({ organizations });
  },

  switchOrganization: async (orgId) => {
    await teamService.switchOrganization(orgId);
    window.location.reload();
  },
}));
